import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ErrorResponseDto, ResponseDto } from 'theSource/types';
import { useApiMutation } from 'theSource/utils';
import { selectedProfileAction } from './slice';
import {
  GET_PROFILE_QUERY_KEY,
  UPDATE_SUBSCRIBER_QUERY_KEY,
  useEnrollmentStatusQuery,
} from 'enrollment/queries';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { useConfigurationQuery } from 'theSource/queries';
import { BottomNavigation, LoaderOverlay } from 'enrollment/components';
import {
  setEnrollmentLatest,
  selectEnrollmentLatest,
  selectProfileSteps,
} from 'enrollment/components/BottomNavigation/services';
import { isNewStatus } from 'enrollment/components/BottomNavigation/utils';

interface SubscriberEditRequestDto {
  subscriber_profile: {
    avatar: string;
    profile_picture: string;
    file_name: string;
  };
}

interface ProfileApiProps {
  navigateTo?: string;
}

export const ProfileApi = ({ navigateTo }: ProfileApiProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { configuration } = useConfigurationQuery();
  const apiHost = configuration?.apiHost;
  const profileSteps = useSelector(selectProfileSteps);

  let futurePages = ['family'];
  if (profileSteps.questionnaire) {
    futurePages = [...futurePages, profileSteps.questionnaire];
  }
  if (profileSteps.decisionSupport === 'decisioniq') {
    futurePages = [...futurePages, profileSteps.decisionSupport];
  }
  if (profileSteps.chatSupport) {
    futurePages = [...futurePages, profileSteps.chatSupport]; // oliveAdvisor POC
  }

  const ProfileApiState = useSelector(
    (state: any) => state?.enrollment?.chooseAvatar
  );
  const fileName = ProfileApiState?.fileName ?? '';
  const chosenDisplay = ProfileApiState?.chosenDisplay ?? '';
  const isCropped = ProfileApiState?.isCropped ?? false;
  const isDisabled = chosenDisplay === 'picture' && !isCropped;
  const croppedImagePath = ProfileApiState?.croppedImagePath ?? '';

  const avatarName = ProfileApiState?.avatar?.name ?? '';

  const avatarSkinTone = ProfileApiState?.avatarSkinTone?.name ?? '';
  const avatarHairColor = ProfileApiState?.avatarHairColor?.name ?? '';
  const avatarShirtColor = ProfileApiState?.avatarShirtColor?.name ?? '';
  const enrollmentLatest = useSelector(selectEnrollmentLatest);
  const enrollmentContext = useSelector(selectEnrollmentContext);
  const { EnrollmentStatusRequest } = useEnrollmentStatusQuery({
    lumosApiHost: String(apiHost),
  });

  const subscriberProfileAvatarValue: string[] = [];
  avatarName && subscriberProfileAvatarValue.push(avatarName);
  avatarSkinTone && subscriberProfileAvatarValue.push(avatarSkinTone);
  avatarHairColor && subscriberProfileAvatarValue.push(avatarHairColor);
  avatarShirtColor && subscriberProfileAvatarValue.push(avatarShirtColor);

  const subscriberProfileAvatarValueString =
    subscriberProfileAvatarValue.join('_');

  const { isLoading, mutate: ProfileBodyRequest } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    SubscriberEditRequestDto
  >({
    queryKey: UPDATE_SUBSCRIBER_QUERY_KEY,
    queryKeysToInvalidate: GET_PROFILE_QUERY_KEY,
    method: 'put',
    endpoint: `${apiHost}/v2/self_service/employees/profile`,
    onSuccess: () => {
      dispatch(selectedProfileAction(avatarName));
      if (
        navigateTo === 'profile' &&
        isNewStatus('profile', enrollmentLatest)
      ) {
        dispatch(setEnrollmentLatest('profile'));
        EnrollmentStatusRequest({
          enrollment_type: enrollmentContext,
          status: 'profile',
        });
      }
      navigate(`/enrollment/${navigateTo}`);
    },
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  // used to disable and re-enable the button
  useEffect(() => {
    const btn = document.getElementById(
      'enrollment-continue-button'
    ) as HTMLElement;

    if (btn === undefined || btn === null) {
      return;
    }

    if (isDisabled) {
      btn.setAttribute('disabled', '');
    } else {
      btn.removeAttribute('disabled');
    }
  });

  const handleSelectedProfile = () => {
    ProfileBodyRequest({
      subscriber_profile: {
        avatar:
          chosenDisplay === 'picture' ? '' : subscriberProfileAvatarValueString,
        profile_picture: chosenDisplay === 'picture' ? croppedImagePath : '',
        file_name: chosenDisplay === 'picture' ? fileName : '',
      },
    });
  };

  return (
    <>
      {isLoading && <LoaderOverlay />}
      <BottomNavigation
        futurePages={futurePages}
        onPage="profile"
        translatedPage="pages.enrollmentProfile"
        onClickEvent={handleSelectedProfile}
      />
    </>
  );
};
